import { formatDate } from "@/utils/utils";
import {
  MASTER_STATUS,
  MASTER_STATUS_LABEL,
  isMasterSelectable,
} from "@/constants/costGl";

export const VAT_GROUP_LABEL = {
  GVAT: "GVAT - General VAT",
  SVAT: "SVAT - Service VAT",
  NOVAT: "NOVAT - No VAT",
  VAT0: "VAT0 - Zero VAT",
  UVAT: "UVAT - Unspecified VAT",
};

// dfType: 0 = Percent, 1 = Fixed (same meaning on the existing master pages).
export const DF_TYPE_PERCENT = 0;

export function formatDf(item) {
  if (!item || item.df === null || item.df === undefined || item.df === "") {
    return "";
  }
  return `${item.df} ${item.dfType === DF_TYPE_PERCENT ? "%" : "บาท"}`;
}

export function formatAccount(account) {
  return account ? `${account.code} - ${account.name}` : "";
}

const th = (key, label, extra = {}) => ({ key, label, sortable: true, ...extra });

// Columns of the accountant table, per master and per tab.
// `actions` is empty on the ยกเลิก tab (read-only), so the column is dropped.
export function buildFields(entityKey, status) {
  const index = { key: "index", label: "", class: "text-center" };
  const state = th("status", "สถานะ", {
    formatter: v => MASTER_STATUS_LABEL[v] || "",
  });
  const code = th("code", "รหัส", { class: "text-nowrap" });
  const costGl = th("costGl", "Cost G/L", {
    formatter: (_v, _k, item) => item.costGl?.code || "",
    sortByFormatted: true,
  });
  const revenueGl = th("revenueGl", "G/L", {
    formatter: (_v, _k, item) => item.revenueGl?.code || "",
    sortByFormatted: true,
  });
  const created = th("creationDt", "วันที่สร้าง", {
    formatter: v => (v ? formatDate(v) : ""),
    class: "text-nowrap",
  });
  const modified = th("modifyDt", "วันที่แก้ไข", {
    formatter: v => (v ? formatDate(v) : ""),
    class: "text-nowrap",
  });
  const price = th("price", "ราคา", { class: "text-right" });
  const category = th("cate", "ประเภท", {
    formatter: (_v, _k, item) => item.cate?.nameTh || "",
    sortByFormatted: true,
  });

  const columns = {
    treat: [
      index, state, code,
      th("nameTh", "รายการรักษา"),
      th("nameEn", "รายการรักษา (Eng)"),
      category, revenueGl, costGl, price,
      th("df", "DF", { formatter: (_v, _k, item) => formatDf(item), sortByFormatted: true }),
      created, modified,
    ],
    lab: [
      index, state, code,
      th("nameTh", "รายการแลป"),
      th("nameEn", "รายการแลป (Eng)"),
      th("companyLab", "บริษัทแลป", {
        formatter: (_v, _k, item) => item.companyLab?.nameTh || "",
        sortByFormatted: true,
      }),
      costGl,
      th("df", "ค่าหักDF", { formatter: (_v, _k, item) => formatDf(item), sortByFormatted: true }),
      th("vatProdPostingGroup", "กลุ่มภาษี VAT"),
      created,
    ],
    item: [
      index, state, code,
      th("nameTh", "รายการสินค้า"),
      th("nameEn", "รายการสินค้า (Eng)"),
      category, revenueGl, costGl, price,
      created, modified,
    ],
  }[entityKey];

  // รอจัดการ rows have no G/L yet, so those columns would always be empty
  const visible =
    status === MASTER_STATUS.PENDING
      ? columns.filter(c => c.key !== "costGl" && c.key !== "revenueGl")
      : columns;

  return status === MASTER_STATUS.CANCELLED
    ? visible
    : [...visible, { key: "actions", label: "", class: "text-center text-nowrap" }];
}

// Read-only rows shown at the top of the "assign Cost G/L" modal.
export function buildModalRows(entityKey, item) {
  if (!item) return [];
  const rows = [
    { label: "รหัส:", value: item.code || "" },
    { label: "ชื่อรายการ (ไทย):", value: item.nameTh || "" },
    { label: "ชื่อรายการ (Eng):", value: item.nameEn || "" },
  ];
  const dfRow = label => ({ label, value: item.df ?? "", dfType: item.dfType, kind: "df" });

  if (entityKey === "treat") {
    rows.push(
      { label: "ประเภท:", value: item.cate?.nameTh || "" },
      { label: "ราคา:", value: item.price ?? "", suffix: "บาท" },
      dfRow("DF รายการรักษา:")
    );
  } else if (entityKey === "lab") {
    rows.push(
      { label: "บริษัทแลป:", value: item.companyLab?.nameTh || "" },
      dfRow("หัก DF:"),
      {
        label: "กลุ่มภาษี VAT:",
        value: VAT_GROUP_LABEL[item.vatProdPostingGroup] || item.vatProdPostingGroup || "",
      }
    );
  } else if (entityKey === "item") {
    rows.push(
      { label: "ราคา:", value: item.price ?? "", suffix: "บาท" },
      { label: "ชื่อหน่วย:", value: item.unitName || "" },
      { label: "ประเภท:", value: item.cate?.nameTh || "" },
      dfRow("ค่าคอมมิชชั่นสินค้า:")
    );
  }
  return rows;
}

// Only ERP chains (clinic type CHAIN) gate masters on a Cost G/L — same rule as
// the BE; other clinic types have no accountant, so every master stays usable.
export const COST_GL_GATED_CLINIC_TYPE = "CHAIN";

/**
 * Masters a user may pick for a NEW patient record. Rows already referenced by
 * records keep resolving elsewhere because the plain lists stay unfiltered.
 * NHSO templates (branchId 0) are shared and never gated.
 *
 * @param {Array} list master rows (treatments / items / labs)
 * @param {string} clinicType e.g. "CHAIN"
 */
export function selectableMasters(list, clinicType) {
  const rows = list || [];
  if (clinicType !== COST_GL_GATED_CLINIC_TYPE) return rows;
  return rows.filter(row => row.branchId === 0 || isMasterSelectable(row));
}
